import React, { useEffect, useMemo, useState } from 'react'
import { Field, change, formValueSelector } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
import { AutoCompleteSelect } from '../../../../../../../../../../itrust_common/components/form'
import SpecteraLensDropdownRow from './SpecteraLensDropdownRow'
import SpecteraLensOptionsSection from './SpecteraLensOptionsSection'
import { LENS_OPTION_ROWS, SPECTERA_CLAIM_FORM } from './constants'
import { specteraLensOptionsConfigurationsLoad } from '../../../../../../../../../../redux/actions/addOns/rpa/vision_insurance/spectera/specteraAction'


const selector = formValueSelector(SPECTERA_CLAIM_FORM)

export default function SpecteraLensesSection(props) {
  const dispatch = useDispatch()
  const {
    fieldConfig,
    formSection,
    lensConfigurations = [],
    claimId,
    locationId,
  } = props

  const reduxClaimId = useSelector(
    (state) => state.visionInsuranceClaimForm?.visionInsuranceClaimForm?.id
  )
  const reduxLocationId = useSelector((state) => {
    const specteraService = state.visionInsurance?.services?.find(
      (item) => item.config.service === 'spectera'
    )
    const matchedLoc = specteraService?.config?.locations?.find(
      (loc) => loc.name === specteraService?.config?.creds?.location
    )
    return matchedLoc?.id || specteraService?.config?.locations?.[0]?.id
  })

  const currentClaimId = claimId || reduxClaimId
  const currentLocationId = locationId || reduxLocationId


const lensOptionsConfigurations = useSelector((state) => {
  const response =
    state.specteraVisionInsurance?.lens_options_configurations ??
    state.specteraVisionInsurance?.os?.lens_options_configurations;

  if (Array.isArray(response)) return response;

  return (
    response?.LensOptionsList ??
    response?.lens_options_configurations?.LensOptionsList ??
    response?.data?.lens_options_configurations?.LensOptionsList ??
    response?.data?.LensOptionsList ??
    []
  );
});
  const {
    showLensType,
    lensStyle,
    lensStyleBillAmount,
    lensMaterial,
    lensMaterialBillAmount,
    lensTreatment,
    lensTreatmentBillAmount,
  } = fieldConfig

  const [LensStyleList, setLensStyleList] = useState([])
  const [LensMaterialList, setLensMaterialList] = useState([])
  const [LensTreatmentList, setLensTreatmentList] = useState([])
  const [lensTypeKey, setLensTypeKey] = useState(null)
  const [lensStyleKey, setLensStyleKey] = useState(null)
  const [lensMaterialKey, setLensMaterialKey] = useState(null)
  const [lensOptionsLoading, setLensOptionsLoading] = useState(false)

  const fieldPath = (name) => `${formSection}.${name}`

  const prescriptionLensType = useSelector((state) =>
    selector(state, 'lab_prescription.prescription_lens_type')
  )
  const selectedStyleVal = useSelector((state) =>
    selector(state, fieldPath(lensStyle))
  )
  const selectedMaterialVal = useSelector((state) =>
    selector(state, fieldPath(lensMaterial))
  )
  const selectedTreatmentVal = useSelector((state) => selector(state, fieldPath(lensTreatment)))

  const safeConfigurations = useMemo(() => {
    return Array.isArray(lensConfigurations)
      ? lensConfigurations
      : (lensConfigurations?.lenses_configurations || [])
  }, [lensConfigurations])

  const matchedConfig = useMemo(() => {
    if (showLensType) return null
    return (
      safeConfigurations.find((c) => String(c?.LENS_TYPE_ID) === String(prescriptionLensType)) ||
      safeConfigurations[0] ||
      null
    )
  }, [showLensType, safeConfigurations, prescriptionLensType])

  const currentStyles = useMemo(() => {
    if (!showLensType) {
      const styles = matchedConfig?.LensStyleList || (safeConfigurations[0]?.LENS_STYLE_ID ? safeConfigurations : [])
      return Array.isArray(styles) ? styles : []
    }
    return Array.isArray(LensStyleList) ? LensStyleList : []
  }, [showLensType, matchedConfig, safeConfigurations, LensStyleList])

  useEffect(() => {
    if (selectedStyleVal && currentStyles.length > 0) {
      const matched = currentStyles.find((s) =>
        s.LENS_STYLE_DESCRIPTION === selectedStyleVal ||
        String(s.LENS_STYLE_ID) === String(selectedStyleVal)
      )
      if (matched) {
        if (matched.LENS_STYLE_DESCRIPTION && selectedStyleVal !== matched.LENS_STYLE_DESCRIPTION) {
          dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(lensStyle), matched.LENS_STYLE_DESCRIPTION))
        }
        if (matched.LensMaterialList?.length > 0 && LensMaterialList.length === 0) {
          setLensMaterialList(matched.LensMaterialList)
          setLensStyleKey(matched.LENS_STYLE_ID || matched.LENS_STYLE_DESCRIPTION)
        }
      }
    }
  }, [selectedStyleVal, currentStyles, LensMaterialList.length])

  useEffect(() => {
    if (selectedMaterialVal && LensMaterialList.length > 0) {
      const matched = LensMaterialList.find((m) =>
        m.LENS_MATERIAL_DESCRIPTION === selectedMaterialVal ||
        String(m.LENS_MATERIAL_ID) === String(selectedMaterialVal)
      )
      if (matched) {
        if (matched.LENS_MATERIAL_DESCRIPTION && selectedMaterialVal !== matched.LENS_MATERIAL_DESCRIPTION) {
          dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(lensMaterial), matched.LENS_MATERIAL_DESCRIPTION))
        }
        if (matched.LensTreatmentList?.length > 0 && LensTreatmentList.length === 0) {
          setLensTreatmentList(matched.LensTreatmentList)
          setLensMaterialKey(matched.LENS_MATERIAL_ID || matched.LENS_MATERIAL_DESCRIPTION)
        }
      }
    }
  }, [selectedMaterialVal, LensMaterialList, LensTreatmentList.length])

  const fetchLensOptions = (lensId) => {
    if (!lensId || !currentClaimId || !currentLocationId) return
    setLensOptionsLoading(true)
    dispatch(
      specteraLensOptionsConfigurationsLoad(currentClaimId, {
        lens_id: lensId,
        location_id: currentLocationId,
        lens_type: 'os',
      })
    ).finally(() => {
      setLensOptionsLoading(false)
    })
  }

  const clearLensOptionsFields = () => {
    LENS_OPTION_ROWS.forEach((num) => {
      dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(`options_${num}`), null))
      dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(`options_${num}_bill_amount`), null))
    })
  }

  const clearLensStyleFields = () => {
    dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(lensStyle), null))
    dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(lensStyleBillAmount), null))
  }

  const clearLensMaterialFields = () => {
    dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(lensMaterial), null))
    dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(lensMaterialBillAmount), null))
  }

  const clearLensTreatmentFields = () => {
    dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(lensTreatment), null))
    dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(lensTreatmentBillAmount), null))
  }

  const handleLensTypeChange = (val) => {
    setLensTypeKey(val?.LENS_TYPE_ID ?? val?.LENS_TYPE_DESCRIPTION ?? null)
    setLensStyleList(val?.LensStyleList || [])
    setLensMaterialList([])
    setLensTreatmentList([])
    setLensStyleKey(null)
    setLensMaterialKey(null)
    clearLensStyleFields()
    clearLensMaterialFields()
    clearLensTreatmentFields()
    clearLensOptionsFields()
  }

  const handleLensStyleChange = (val) => {
    setLensStyleKey(val?.LENS_STYLE_ID ?? val?.LENS_STYLE_DESCRIPTION ?? null)
    setLensMaterialList(val?.LensMaterialList || [])
    setLensTreatmentList([])
    setLensMaterialKey(null)
    clearLensMaterialFields()
    clearLensTreatmentFields()
    clearLensOptionsFields()
  }

  const handleLensMaterialChange = (val) => {
    setLensMaterialKey(val?.LENS_MATERIAL_ID ?? val?.LENS_MATERIAL_DESCRIPTION ?? null)
    setLensTreatmentList(val?.LensTreatmentList || [])
    clearLensTreatmentFields()
    clearLensOptionsFields()
  }

  const handleLensTreatmentChange = (val) => {
    const lensId = val?.LENS_ID || val?.lens_id
    if (lensId) {
      fetchLensOptions(lensId)
    }
  }

  useEffect(() => {
    if (selectedTreatmentVal && LensTreatmentList.length > 0) {
      const treatmentItem =
        typeof selectedTreatmentVal === 'object'
          ? selectedTreatmentVal
          : LensTreatmentList.find((t) =>
              t.LENS_TREATMENT_DESCRIPTION === selectedTreatmentVal ||
              String(t.LENS_TREATMENT_ID) === String(selectedTreatmentVal)
            )
      if (treatmentItem) {
        if (treatmentItem.LENS_TREATMENT_DESCRIPTION && selectedTreatmentVal !== treatmentItem.LENS_TREATMENT_DESCRIPTION) {
          dispatch(change(SPECTERA_CLAIM_FORM, fieldPath(lensTreatment), treatmentItem.LENS_TREATMENT_DESCRIPTION))
        }
        const lensId = treatmentItem?.LENS_ID || treatmentItem?.lens_id
        if (lensId && (!lensOptionsConfigurations || lensOptionsConfigurations.length === 0)) {
          fetchLensOptions(lensId)
        }
      }
    }
  }, [selectedTreatmentVal, LensTreatmentList, currentClaimId, currentLocationId, lensOptionsConfigurations])

  return (
    <>
      {/* {showIndustrialThickness && (
        <div className='row mt-6'>
          <RadioGroup
            name={industrialThickness}
            options={INDUSTRIAL_THICKNESS_OPTIONS}
            wrapperClasses="form-group col-6"
            label="Industrial Thickness"
          />
        </div>
      )} */}
      {showLensType && (
        <div className='row mt-6'>
          <Field
            name="lens_type"
            valueField="LENS_TYPE_DESCRIPTION"
            textField="LENS_TYPE_DESCRIPTION"
            label="Lens Type"
            data={lensConfigurations}
            key={lensConfigurations?.length || 0}
            component={AutoCompleteSelect}
            onChangeHook={handleLensTypeChange}
            wrapperClasses="form-group col-6"
          />
          <span className='col-3' />
        </div>
      )}

      <SpecteraLensDropdownRow
        name={lensStyle}
        label="Lens Style"
        valueField="LENS_STYLE_DESCRIPTION"
        textField="LENS_STYLE_DESCRIPTION"
        billAmountName={lensStyleBillAmount}
        data={currentStyles || []}
        fieldKey={matchedConfig?.LENS_TYPE_ID || lensTypeKey}
        onChangeHook={handleLensStyleChange}
        formSection={formSection}
      />
      <SpecteraLensDropdownRow
        name={lensMaterial}
        label="Lens Material"
        billAmountName={lensMaterialBillAmount}
        valueField="LENS_MATERIAL_DESCRIPTION"
        textField="LENS_MATERIAL_DESCRIPTION"
        data={LensMaterialList || []}
        fieldKey={lensStyleKey}
        onChangeHook={handleLensMaterialChange}
        formSection={formSection}
      />
      <SpecteraLensDropdownRow
        name={lensTreatment}
        label="Lens Treatment"
        valueField="LENS_TREATMENT_DESCRIPTION"
        textField="LENS_TREATMENT_DESCRIPTION"
        billAmountName={lensTreatmentBillAmount}
        fieldKey={lensMaterialKey}
        data={LensTreatmentList || []}
        onChangeHook={handleLensTreatmentChange}
        formSection={formSection}
      />

      <SpecteraLensOptionsSection
        data={lensOptionsConfigurations}
        formSection={formSection}
        loading={lensOptionsLoading}
        key={lensOptionsConfigurations?.length || 0}
      />
    </>
  )
}
